import React, { useRef, useEffect, useState } from "react";
import { Debug } from "./debug";
import { Box } from "@mui/material";
import { Row } from "./row";

// --- Global registry ---
const renderRegistry = new Map(); // label -> { count, mounts, lastUpdate, status }
const subscribers = new Set();
const deletionTimers = new Map();

function notify() {
  for (const fn of subscribers) fn();
}

// --- Hook ---
export function useRenderCounter(label) {
  const renderCount = useRef(0);
  renderCount.current += 1;
  const id = label || "Unnamed";

  useEffect(() => {
    const pending = deletionTimers.get(id);

    if (pending) {
      clearTimeout(pending);
      deletionTimers.delete(id);

      // Fast remount: keep red flash
      const prev = renderRegistry.get(id);
      if (prev?.status === "unmounting") {
        renderRegistry.set(id, {
          ...prev,
          status: "reviving",
          lastUpdate: Date.now(),
          mounts: prev.mounts + 1, // increment mount count
        });
        notify();
        return;
      }
    }

    // Normal render or first mount
    const prev = renderRegistry.get(id);
    renderRegistry.set(id, {
      count: renderCount.current,
      lastUpdate: Date.now(),
      status: "active",
      mounts: prev ? prev.mounts : 1, // initialize mount count
    });
    notify();
  });

  useEffect(() => {
    return () => {
      const entry = renderRegistry.get(id);
      if (entry) {
        renderRegistry.set(id, {
          ...entry,
          status: "unmounting",
          lastUpdate: Date.now(),
        });
        notify();

        const timer = setTimeout(() => {
          const current = renderRegistry.get(id);
          if (current?.status === "unmounting") {
            renderRegistry.delete(id);
            notify();
          }
          deletionTimers.delete(id);
        }, 1000);

        deletionTimers.set(id, timer);
      }
    };
  }, [id]);
}

// --- Overlay ---
export function RenderCounter() {
  const [, forceUpdate] = useState({});
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const update = () => forceUpdate({});
    subscribers.add(update);
    return () => subscribers.delete(update);
  }, []);

  const now = Date.now();
  const entries = Array.from(renderRegistry.entries()).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  const containerStyle = {
    position: "fixed",
    bottom: "10px",
    right: "10px",
    background: "rgba(0,0,0,0.85)",
    fontFamily: "monospace",
    fontSize: "12px",
    padding: "10px 12px",
    borderRadius: "10px",
    zIndex: 9999,
    maxHeight: "40vh",
    overflowY: "auto",
    "*": { color: "#00ff88" },
  };

  const keyframes = `
    @keyframes flash-remount {
      0% { background: rgba(255,0,0,0.4); }
      40% { background: rgba(255,100,0,0.35); }
      80% { background: rgba(255,255,0,0.3); }
      100% { background: transparent; }
    }
  `;

  return (
    <Debug sx={containerStyle}>
      <style>{keyframes}</style>
      <Box className="RenderCounter">
        <Row
          style={{ fontWeight: "bold", marginBottom: "4px" }}
          className="Header"
        >
          <Box
            onClick={() => setOpen((open) => !open)}
            sx={{ cursor: "pointer" }}
            title="Render counter"
          >
            🔄
          </Box>
          {open && <Box>Render & Mount Counters</Box>}
        </Row>
        {open && (
          <Box className="content">
            {entries.length === 0 ? (
              <div style={{ opacity: 0.5 }}>No tracked components</div>
            ) : (
              entries.map(([label, { count, mounts, lastUpdate, status }]) => {
                const age = now - lastUpdate;
                const isFresh = age < 800;

                let background = "transparent";
                let animation = "";

                if (status === "unmounting")
                  background = "rgba(255, 0, 0, 0.4)";
                else if (status === "reviving")
                  animation = "flash-remount 1s ease-out";
                else if (isFresh) background = "rgba(255, 255, 0, 0.3)";

                return (
                  <div
                    key={label}
                    style={{
                      marginBottom: "2px",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      background,
                      animation,
                      transition:
                        "background 0.5s ease-out, opacity 0.6s ease-out, transform 0.4s ease-out",
                      opacity: status === "unmounting" ? 0.6 : 1,
                      transform:
                        status === "unmounting"
                          ? "translateX(8px)"
                          : status === "reviving"
                          ? "translateX(4px)"
                          : "none",
                    }}
                  >
                    {label}: Renders {count}, Mounts {mounts}
                  </div>
                );
              })
            )}
          </Box>
        )}
      </Box>
    </Debug>
  );
}
